import { QueryClient } from '@tanstack/react-query';

/**
 * Application-wide React Query client.
 *
 * Lives outside `App.tsx` so non-React code (e.g. the logout path in
 * `firebase/auth.ts` and `stores/authStore.ts`) can purge the cache directly.
 *
 * CRITICAL: the generated API `queryKeyFn` builds keys from the URL path +
 * query params + body only — no viewer identity. Because logout is SPA
 * navigation (no full page reload), a cache that survives a logout would serve
 * the previous account's notes/user/subscription data to the next user with no
 * network call (`refetchOnMount: false` + 5 min `staleTime`). Every logout path
 * MUST call `clearQueryCache()`.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refetch when window regains focus - reduces unnecessary API calls
      refetchOnWindowFocus: false,
      // Don't refetch when component remounts if data exists
      refetchOnMount: false,
      // Consider data fresh for 5 minutes before refetching
      staleTime: 5 * 60 * 1000,
      // Keep data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Retry failed requests only once (not 3 times)
      retry: 1,
    },
    mutations: {
      // Don't retry failed mutations
      retry: false,
    },
  },
});

/**
 * Purge every cached query so no data can leak across an account switch.
 * Safe to call from any logout path — never throws.
 */
export const clearQueryCache = (): void => {
  try {
    queryClient.clear();
  } catch {
    // Cache clearing is best-effort — never block logout.
  }
};
